import {
  DesktopDateTimePicker,
  LocalizationProvider,
  TimeView,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import TimeLineProgress from './TimeLineProgress';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import useGetSiteInfo from 'services/common/useGetSiteInfo';
import { PositionWithExpiryData } from '../OptionChain/types';

const TimeLineContent = ({
  date,
  setDate,
  isOnlyOptionChain = false,
  symbol,
  setSymbol,
  expiry,
  setExpiry,
  symbols = [],
  expiries = [],
  positions = {},
  spotPrice,
}: {
  date: string;
  setDate: (date: string) => void;
  isOnlyOptionChain?: boolean;
  symbol?: string;
  setSymbol?: (symbol: string) => void;
  expiry?: string;
  setExpiry?: (expiry: string) => void;
  symbols?: string[];
  expiries?: string[];
  positions?: PositionWithExpiryData;
  spotPrice?: number;
}) => {
  const handleClickDate = (selectedDate: moment.Moment | null) => {
    if (selectedDate) {
      const formattedDate = selectedDate.format('YYYY-MM-DD HH:mm:ss ');
      setDate(formattedDate);
    }
  };

  const handleSymbolChange = (event: SelectChangeEvent) => {
    if (setSymbol) {
      setSymbol(event.target.value);
    }
  };

  const handleExpiryChange = (event: SelectChangeEvent) => {
    if (setExpiry) {
      setExpiry(event.target.value);
    }
  };

  const disableWeekends = (selectedDate: moment.Moment | null) => {
    return selectedDate?.day() === 0 || selectedDate?.day() === 6;
  };
  const disableTimes = (
    selectedDate: moment.Moment | null,
    timeView: TimeView,
  ) => {
    if (selectedDate) {
      const hours = selectedDate.hours();
      const minutes = selectedDate.minutes();
      if (timeView === 'hours') {
        return hours < 9 || hours > 15;
      } else if (timeView === 'minutes') {
        return (hours === 9 && minutes < 16) || (hours === 15 && minutes > 29);
      }
    }
    return false;
  };
  const { data: siteInfo } = useGetSiteInfo();

  return (
    <Box
      {...(isOnlyOptionChain
        ? {
            mt: 1,
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            display: 'flex',
          }
        : { display: 'flex', mt: 1, alignItems: 'center' })} // ensure vertical alignment and spacing
    >
      {/* Symbol Selection */}
      {isOnlyOptionChain && symbols.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Symbol</InputLabel>
          <Select
            value={symbol || ''}
            label="Symbol"
            onChange={handleSymbolChange}
            sx={{
              '& .MuiOutlinedInput-input': {
                py: 2.5,
              },
            }}
            renderValue={selected => (
              <Box display={'flex'}>
                <Typography variant="body2">{selected}</Typography>
                {spotPrice && (
                  <Typography variant="body2" ml={1}>
                    {spotPrice}
                  </Typography>
                )}
              </Box>
            )}
          >
            {symbols.map(sym => (
              <MenuItem key={sym} value={sym}>
                {sym}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Expiry Selection */}
      {isOnlyOptionChain && expiries.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Expiry</InputLabel>
          <Select
            value={expiry || ''}
            label="Expiry"
            onChange={handleExpiryChange}
            sx={{
              '& .MuiOutlinedInput-input': {
                py: 2.5,
              },
            }}
          >
            {expiries.map(exp => (
              <MenuItem key={exp} value={exp}>
                {moment(exp).format('DD MMM YYYY')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Date Picker */}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DesktopDateTimePicker
          label={'Date'}
          format="YYYY-MM-DD HH:mm"
          value={moment(date, 'YYYY-MM-DD HH:mm:ss')}
          onChange={handleClickDate}
          disableFuture
          shouldDisableDate={disableWeekends}
          shouldDisableTime={disableTimes}
          timeSteps={{ minutes: 1 }}
          minDateTime={moment(
            siteInfo?.optionchain_min_date || '2025-05-01 09:16:00',
            'YYYY-MM-DD HH:mm:ss',
          )}
          maxDateTime={moment()
            .subtract(1, 'day')
            .set({ hour: 15, minute: 29, second: 0 })}
          ampm={false}
          sx={{
            '& .MuiMultiSectionDigitalClockSection-root': {
              overflowY: 'hidden !important',
            },
            '& .MuiOutlinedInput-input': {
              py: 2.5,
              minHeight: 24,
            },
            minHeight: 56,
            display: 'flex',
            alignItems: 'center',
          }}
          slotProps={{
            digitalClockSectionItem: {
              sx: {
                scrollbarWidth: 'none',
                overflowY: 'hidden  ',
                '::-webkit-scrollbar': {
                  width: '1px',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
      {!isOnlyOptionChain && (
        <TimeLineProgress date={date} setDate={setDate} positions={positions} />
      )}
    </Box>
  );
};

export default TimeLineContent;
